import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_DECKS, GENERATE_FLASHCARDS } from '../graphql';
import { Zap, ChevronLeft, ChevronRight, RotateCcw, BookOpen } from 'lucide-react';

function FlashcardViewer({ cards }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  if (!cards.length) return null;
  const card = cards[idx];
  return (
    <div>
      <div className="relative h-52 cursor-pointer mb-4" onClick={() => setFlipped(!flipped)}
        style={{ perspective: '1000px' }}>
        <div className={`absolute inset-0 transition-transform duration-500`}
          style={{ transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : '' }}>
          {/* Front */}
          <div className="absolute inset-0 rounded-2xl flex items-center justify-center p-6 text-white"
            style={{ background: '#1257a0', backfaceVisibility: 'hidden' }}>
            <div className="text-center">
              {card.topic && <p className="text-brand-200 text-xs mb-2 uppercase tracking-wide">{card.topic}</p>}
              <p className="text-lg font-semibold">{card.question}</p>
              <p className="text-white/50 text-xs mt-4">Tap to reveal answer</p>
            </div>
          </div>
          {/* Back */}
          <div className="absolute inset-0 bg-white border-2 border-indigo-200 rounded-2xl flex items-center justify-center p-6"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
            <p className="text-gray-800 text-center">{card.answer}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => { setIdx(Math.max(0, idx - 1)); setFlipped(false); }}
          disabled={idx === 0} className="btn-secondary flex items-center gap-1 disabled:opacity-40">
          <ChevronLeft size={18} /> Prev
        </button>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{idx + 1} / {cards.length}</span>
          <button onClick={() => setFlipped(false)} className="p-2 text-gray-400 hover:text-gray-600">
            <RotateCcw size={16} />
          </button>
        </div>
        <button onClick={() => { setIdx(Math.min(cards.length - 1, idx + 1)); setFlipped(false); }}
          disabled={idx === cards.length - 1} className="btn-secondary flex items-center gap-1 disabled:opacity-40">
          Next <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

export default function FlashcardsPage() {
  const { data, loading } = useQuery(MY_DECKS);
  const [generate, { loading: generating }] = useMutation(GENERATE_FLASHCARDS, { refetchQueries: [MY_DECKS] });
  const [form, setForm] = useState({ text: '', title: '', subject: '' });
  const [showForm, setShowForm] = useState(false);
  const [activeDeck, setActiveDeck] = useState(null);
  const [result, setResult] = useState(null);

  const handleGenerate = async (e) => {
    e.preventDefault();
    const { data } = await generate({ variables: form });
    setResult(data.generateFlashcardsFromText);
    setShowForm(false);
    setActiveDeck(null);
  };

  const decks = data?.myFlashcardDecks || [];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Flashcards</h1>
          <p className="text-gray-500 text-sm mt-1">Paste notes → get flashcards, quiz & revision sheet</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => { setShowForm(!showForm); setResult(null); }}>
          <Zap size={18} /> Generate
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleGenerate} className="card mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Deck Title</label>
              <input className="input" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Data Structures Chapter 3" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject (optional)</label>
              <input className="input" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Paste your notes</label>
            <textarea className="input !h-48 resize-none" required value={form.text}
              onChange={e => setForm({...form, text: e.target.value})}
              placeholder="Paste text, notes, or content to generate flashcards from..." />
          </div>
          <div className="flex gap-3">
            <button className="btn-primary flex items-center gap-2" type="submit" disabled={generating}>
              {generating ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Generating...</> : <><Zap size={18} /> Generate with AI</>}
            </button>
            <button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* AI Result */}
      {result && (
        <div className="mb-8 space-y-6">
          <div className="card">
            <h3 className="font-semibold mb-4 dark:text-white">Flashcards — {result.deck.title}</h3>
  <FlashcardViewer cards={result.deck.cards} />
          </div>
          <div className="card">
            <h3 className="font-semibold mb-3 dark:text-white">Revision Sheet</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{result.revisionSheet}</p>
          </div>
          <div className="card">
            <h3 className="font-semibold mb-4 dark:text-white">Quick Quiz</h3>
            <div className="space-y-4">
              {result.quiz.map((q, qi) => (
                <QuizItem key={qi} question={q} index={qi} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Saved decks */}
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Decks</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {decks.map(deck => (
          <div key={deck.id} className="card cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveDeck(activeDeck?.id === deck.id ? null : deck)}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-gray-900">{deck.title}</h3>
                {deck.subject && <p className="text-xs text-gray-500">{deck.subject}</p>}
              </div>
              <span className="badge bg-indigo-50 text-indigo-700">{deck.cards.length} cards</span>
            </div>
            {activeDeck?.id === deck.id && (
              <div className="mt-4 border-t pt-4">
                <FlashcardViewer cards={deck.cards} />
              </div>
            )}
          </div>
        ))}
        {decks.length === 0 && !result && (
          <div className="col-span-2 text-center py-16 text-gray-400">
            <BookOpen size={48} className="mx-auto mb-3 opacity-30" />
            <p>No decks yet. Generate your first AI flashcard set!</p>
          </div>
        )}
      </div>
    </div>
  );
}

function QuizItem({ question, index }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <p className="font-medium text-sm mb-3">{index + 1}. {question.question}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {question.options.map((opt, oi) => {
          let cls = 'text-left px-3 py-2 rounded-lg text-sm border transition-colors ';
          if (selected === null) cls += 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50';
          else if (oi === question.correctIndex) cls += 'border-green-500 bg-green-50 text-green-700 font-medium';
          else if (oi === selected) cls += 'border-red-400 bg-red-50 text-red-600';
          else cls += 'border-gray-200 text-gray-400';
          return <button key={oi} className={cls} onClick={() => selected === null && setSelected(oi)}>{opt}</button>;
        })}
      </div>
      {selected !== null && question.explanation && (
        <p className="text-xs text-gray-600 mt-2 bg-blue-50 px-3 py-2 rounded-lg">{question.explanation}</p>
      )}
    </div>
  );
}
